import { Box, Checkbox, Stack, Typography } from "@mui/material";
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_CHECKBOX_SELECTION_FIELD,
  GridColDef,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridRow,
  GridRowHeightParams,
  GridRowHeightReturnValue,
  GridRowProps,
  GridRowSelectionModel,
  GridSortModel,
  GridValidRowModel,
  DataGrid as MuiDataGrid,
  DataGridProps as MuiDataGridProps,
  RowPropsOverrides,
  useGridApiRef,
} from "@mui/x-data-grid";
import { GridStateCommunity } from "@mui/x-data-grid/models/gridStateCommunity";
import { chain, endsWith, isNil, startsWith } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  CustomDataGridContext,
  CustomDataGridContextType,
  CustomDataGridContextValue,
} from "@/common/contexts/customDataGrid";
import { PaginationHelper } from "@/common/helpers/pagination";
import { SortHelper } from "@/common/helpers/sort";
import { TypeHelper } from "@/common/helpers/type";
import {
  DataGridBorderVariant,
  DataGridColumnVisibilityProps,
  DataGridColumnWidthProps,
  DataGridCustomSortProps,
  DataGridFilterProps,
  DataGridPaginationProps,
  DataGridQuickFilterProps,
  DataGridRefetchProps,
  DataGridScrollProps,
  GridRowHeightParamsCustom,
} from "@/common/ts/dataGrid";

import NoDataAlert from "../AppAlerts/NoDataAlert";
import AppVisibilitySensor from "../AppVisibilitySensor";
import AppIconButton from "../Button/AppIconButton";
import { DataListTabsProps } from "../DataList/DataList";
import AppIcon from "../Icons/AppIcon";
import CustomDataGridColumnMenu from "./CustomDataGridColumnMenu";
import CustomDataGridFooter from "./CustomDataGridFooter";
import CustomDataGridPagination from "./CustomDataGridPagination";
import CustomDataGridToolbar from "./CustomDataGridToolbar";

const rowCheckboxSelectionField = GRID_CHECKBOX_SELECTION_FIELD; // by default "__check__"
const rowNumberField = "__rowNumber__";
export const gridInternalFields = [rowCheckboxSelectionField, rowNumberField];
export const gridInternalFieldsMap = chain(gridInternalFields)
  .keyBy((x) => x)
  .mapValues((x) => true)
  .value();

/** MUI X: `pageSize` cannot exceed 100 in the MIT version of the DataGrid.
 * https://mui.com/x/react-data-grid/pagination/#size-of-the-page
 */
export const dataGridMaxPageSize = 100;
export const dataGridMaxSortItems = 1;
export const dataGridMaxFilterItems = 1;

export interface DataGridProps<TItem extends GridValidRowModel> {
  sx?: MuiDataGridProps<TItem>["sx"];
  columns: GridColDef<TItem>[];
  rows: TItem[] | null | undefined;
  isLoading?: boolean;
  /** Enables row selection via checkboxes.
   * @default false
   */
  isRowCheckboxSelectionEnabled?: boolean;
  /** Enables row numbers.
   * @default true
   */
  isRowNumbersEnabled?: boolean;
  /** Enables column menu.
   * @default true
   */
  isColumnMenuEnabled?: boolean;
  /** Enables column resize.
   * @default true
   */
  isColumnSelectorEnabled?: boolean;
  /** Enables column resize.
   * @default true
   */
  isColumnResizeEnabled?: boolean;
  /** Enables columns sort.
   * @default true
   */
  isColumnSortEnabled?: boolean;
  /** Enables column filter.
   * @default true
   */
  isColumnFilterEnabled?: boolean;
  /** Enables pagination.
   * @default true
   */
  isPaginationEnabled?: boolean;
  isAutoHeight?: boolean;
  rowTo?: string | ((item: TItem) => string);
  columnVisibility?: DataGridColumnVisibilityProps;
  columnWidth?: DataGridColumnWidthProps;
  tabs?: DataListTabsProps;
  pagination?: DataGridPaginationProps;
  sort?: DataGridCustomSortProps;
  quickFilter?: DataGridQuickFilterProps;
  filters?: DataGridFilterProps;
  scroll?: DataGridScrollProps;
  refetch?: DataGridRefetchProps;
  rowSelectionModel?: GridRowSelectionModel;
  // GridRowSelectionModel: <string[] | number[]>
  /** Sets the height in pixel of a row in the grid. */
  rowHeight?: number;
  /** @default none */
  borderVariant?: DataGridBorderVariant;
  getRowId: (item: TItem) => string;
  getRowIsHighlighted?: (item: TItem) => boolean;
  onRowClick?: (item: TItem) => void | Promise<void>;
  onRowSelectionModelChange?: (newValue: GridRowSelectionModel) => void;
  /** Function that sets the row height per row. */
  getRowHeight?: (params: GridRowHeightParamsCustom<TItem>) => GridRowHeightReturnValue;
}

export default function DataGrid<TItem extends GridValidRowModel>({
  sx,
  columns,
  rows,
  isLoading = false,
  isRowCheckboxSelectionEnabled = false,
  isRowNumbersEnabled = true,
  isColumnMenuEnabled = true,
  isColumnSelectorEnabled = true,
  isColumnResizeEnabled = true,
  isColumnSortEnabled = true,
  isColumnFilterEnabled = true,
  isPaginationEnabled = true,
  isAutoHeight = true,
  rowTo,
  columnVisibility,
  columnWidth,
  tabs,
  pagination,
  sort,
  quickFilter,
  filters,
  scroll,
  refetch,
  rowHeight = 52, // 52 is default MUI value
  rowSelectionModel,
  borderVariant = "none",
  getRowId,
  getRowIsHighlighted,
  onRowClick,
  getRowHeight,
  onRowSelectionModelChange,
}: DataGridProps<TItem>) {
  const apiRef = useGridApiRef();
  const scrollEndAnchorRef = useRef<HTMLDivElement>(null);
  const scrollEndIsVisible = useRef({
    index: 0,
    isVisible: false,
  });

  // This useEffect updates the `scrollEndIsVisible` index whenever the number of rows or the `scrollEndIndexOffset` changes.
  // It calculates the index of the row that triggers the "scroll end" event based on the total number of rows and the threshold.
  // This ensures that the event is triggered correctly even if the number of rows changes dynamically.
  useEffect(() => {
    scrollEndIsVisible.current.index = (rows?.length || 0) - (scroll?.scrollEndIndexOffset || 0);
  }, [rows?.length, scroll?.scrollEndIndexOffset]);

  const initialPaginationModelComputed = useMemo<GridPaginationModel | undefined>(
    () =>
      PaginationHelper.mapPaginationInfoToGridPaginationModel(pagination?.initialPaginationInfo),
    [],
  );

  const initialRowCountComputed = useMemo<number | undefined>(
    () => pagination?.initialPaginationInfo?.totalCount ?? 0,
    [],
  );

  const isColumnSortEnabledComputed = useMemo<boolean>(
    () => isColumnSortEnabled && !TypeHelper.isEmpty(sort),
    [isColumnSortEnabled, sort],
  );
  const initialSortModelComputed = useMemo<GridSortModel | undefined>(
    () => SortHelper.mapSortDefinitionToGridSortModel(sort?.initialSortDefinition),
    [],
  );

  const isColumnFilterEnabledComputed = useMemo<boolean>(
    () => isColumnFilterEnabled && !TypeHelper.isEmpty(filters),
    [isColumnFilterEnabled, filters],
  );

  const isColumnMenuEnabledComputed = useMemo<boolean>(
    () => isColumnMenuEnabled,
    [isColumnMenuEnabled],
  );

  const isPaginationEnabledComputed = useMemo<boolean>(
    () => isPaginationEnabled && !!pagination,
    [isPaginationEnabled, pagination],
  );

  const [customDataGridContext] = useState<CustomDataGridContextType>(
    new CustomDataGridContextValue(),
  );
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    columnVisibility?.initialMap ?? {},
  );
  const [sortModel, setSortModel] = useState<GridSortModel | undefined>(initialSortModelComputed);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel | undefined>(
    initialPaginationModelComputed,
  );
  const [rowCount, setRowCount] = useState<number | undefined>(initialRowCountComputed);

  const sortComputed = useMemo<DataGridCustomSortProps | undefined>(
    () =>
      sort
        ? {
            ...sort,
            maxSortItems: dataGridMaxSortItems,
            onChange: (newValue) => {
              // sync SortDefinition -> GridSortModel
              const newSortModel = SortHelper.mapSortDefinitionToGridSortModel(newValue);
              setSortModel(newSortModel);
              sort.onChange(newValue);
            },
          }
        : undefined,
    [sort?.sortDefinition],
  );

  // columns must be memorized once and do not change between renders
  const columnsComputed = useMemo<GridColDef<TItem>[]>(() => {
    const newColumns: GridColDef<TItem>[] = columns.map((column) => ({
      ...column,
      width: columnWidth?.initialMap
        ? (columnWidth?.initialMap[column.field] ?? column.width)
        : column.width,
    }));

    // custom row selection & row number column:
    // 1. just row selection
    // 2. just row number
    // 3. row selection and row row number in a single column
    if (isRowCheckboxSelectionEnabled || isRowNumbersEnabled) {
      newColumns.unshift({
        ...(isRowCheckboxSelectionEnabled
          ? GRID_CHECKBOX_SELECTION_COL_DEF
          : {
              field: rowNumberField,
              headerName: "#",
              headerAlign: "center",
              description: "Row number",
              width: 50,
              maxWidth: 50,
              hideable: false,
              // resizable: false, // DataGrid Pro
              isSortable: false,
              filterable: false,
              isColumnMenuDisabled: true,
            }),
        // width: 50,
        // flex: 0,
        renderCell: (params) => {
          const rowId = params.id;
          const isRowSelected = params.api.isRowSelected(rowId);
          const isRowNumberVisible =
            isRowNumbersEnabled &&
            (!isRowCheckboxSelectionEnabled || (isRowCheckboxSelectionEnabled && !isRowSelected));
          const isRowNumberAlwaysVisible = isRowNumbersEnabled && !isRowCheckboxSelectionEnabled;
          const isRowCheckboxVisible =
            isRowCheckboxSelectionEnabled &&
            (!isRowNumbersEnabled || (isRowCheckboxSelectionEnabled && isRowSelected));
          const isRowCheckboxAlwaysVisible = isRowCheckboxSelectionEnabled && !isRowNumbersEnabled;

          return (
            <Stack
              sx={{ justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}
            >
              {/* Row number */}
              {isRowNumbersEnabled && (
                <Typography
                  sx={{
                    display: isRowNumberVisible ? "block" : "none",
                    ...(isRowNumberAlwaysVisible ? { display: "block !important" } : undefined),
                  }}
                  className='MuiDataGrid-customRowNumber'
                  variant='body2'
                >
                  {params.api.getAllRowIds().indexOf(rowId) + 1}
                </Typography>
              )}

              {/* Row selection checkbox */}
              {isRowCheckboxSelectionEnabled && (
                <Box
                  sx={{
                    display: isRowCheckboxVisible ? "block" : "none",
                    ...(isRowCheckboxAlwaysVisible ? { display: "block !important" } : undefined),
                  }}
                  className='MuiDataGrid-customRowSelectionCheckbox'
                >
                  <Checkbox
                    checked={params.api.isRowSelected(rowId)}
                    onChange={(e) => {
                      e.stopPropagation();
                      const isRowSelectedNew = e.target.checked;
                      params.api.selectRow(rowId, isRowSelectedNew);
                    }}
                  />
                </Box>
              )}
            </Stack>
          );
        },
      });
    }

    return newColumns;
  }, [rowSelectionModel]);

  // track current pagination change from outside
  useEffect(() => {
    // const newPaginationModel = PaginationHelper.mapPaginationInfoToGridPaginationModel(
    //   pagination?.currentPaginationInfo,
    // );
    // if (!isDeepEqual(paginationModel, newPaginationModel)) {
    //   setPaginationModel(newPaginationModel);
    // }

    const newRowCount = pagination?.currentPaginationInfo?.totalCount ?? 0;
    if (rowCount !== newRowCount) {
      // console.log("DataGrid.onPaginationInfoChangeFromOutside", {
      //   rowCount,
      //   newRowCount,
      //   pagination,
      // });
      setRowCount(newRowCount);
    }
  }, [pagination?.currentPaginationInfo]);

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    newModel.pageSize = Math.min(newModel.pageSize, PaginationHelper.maxLimitDataGrid);
    const newState: GridStateCommunity = {
      ...apiRef.current.state,
      pagination: {
        ...apiRef.current.state.pagination,
        paginationModel: newModel,
      },
    };
    setPaginationModel(newModel);

    const newPaginationInfo = PaginationHelper.mapGridPaginationStateToPaginationInfo(
      newState.pagination,
    );

    // console.log("DataGrid.onPaginationModelChange", {
    //   newModel,
    //   newState,
    //   newPaginationInfo,
    // });

    pagination?.onChange && pagination?.onChange(newPaginationInfo);
  };

  // console.log("DataGrid.", { columnsComputed, columnWidth });
  // console.log("DataGrid.", {
  //   columnVisibilityModel,
  //   sortModel,
  //   isPaginationEnabledComputed,
  //   initialPaginationModelComputed,
  //   paginationModel,
  //   rowCount,
  // });

  const scrollEndIndexOffsetIndex = useMemo(() => {
    if (scroll?.scrollEndIndexOffset) {
      const rs = rows || [];
      return rs.length > scroll.scrollEndIndexOffset
        ? rs.length - scroll.scrollEndIndexOffset
        : rs.length - 1;
    }
    return undefined;
  }, [rows, scroll?.scrollEndIndexOffset]);

  const CustomRow = useCallback(
    (rowParams: GridRowProps & RowPropsOverrides) => {
      const isTargetRow = rowParams.index === scrollEndIndexOffsetIndex;
      if (isTargetRow) {
        return (
          <>
            <AppVisibilitySensor
              onChange={(isVisible) => {
                const currentIndex = rowParams.index;
                const prevIsVisible = scrollEndIsVisible.current.isVisible;
                const prevIndex = scrollEndIsVisible.current.index;

                if (isVisible && !prevIsVisible && prevIndex === currentIndex) {
                  scroll?.onScrollEnd?.();
                }

                scrollEndIsVisible.current.isVisible = isVisible;
              }}
            >
              {(params) => <Box {...params.observableProps} ref={scrollEndAnchorRef}></Box>}
            </AppVisibilitySensor>
            <GridRow {...rowParams} />
          </>
        );
      }
      return <GridRow {...rowParams} />;
    },
    [scrollEndIndexOffsetIndex, scroll?.onScrollEnd],
  );

  // #region Horizontal scroll buttons logic

  const gridRef = useRef<HTMLDivElement>(null);
  const [gridMainElement, setGridMainElement] = useState<HTMLDivElement | null | undefined>(null);
  const [gridScrollbarElement, setGridScrollbarElement] = useState<
    HTMLDivElement | null | undefined
  >(null);
  const [mainAppContentElement, setMainAppContentElement] = useState<
    HTMLDivElement | null | undefined
  >(null);
  const buttonDefaultOffset = 100;
  const [isLeftButtonVisible, setIsLeftButtonVisible] = useState(false);
  const [isRightButtonVisible, setIsRightButtonVisible] = useState(false);
  const leftButtonRef = useRef<HTMLButtonElement>(null);
  const rightButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (gridRef.current) {
      const gridMain = gridRef.current.querySelector(".MuiDataGrid-main");
      if (gridMain) {
        setGridMainElement(gridMain as HTMLDivElement);
      }
      const gridScrollbar = gridRef.current.querySelector(".MuiDataGrid-scrollbar--horizontal");
      if (gridScrollbar) {
        setGridScrollbarElement(gridScrollbar as HTMLDivElement);
      }
      const appContent = document.querySelector(".AppMainContent");
      if (appContent) {
        setMainAppContentElement(appContent as HTMLDivElement);
      }
    }
  }, [gridRef.current]);

  const handleScrollRightClick = () => {
    if (gridScrollbarElement) {
      gridScrollbarElement.scrollBy({ left: 200, behavior: "smooth" });
    }
  };
  const handleScrollLeftClick = () => {
    if (gridScrollbarElement) {
      gridScrollbarElement.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  // #region Update arrow buttons visibility(show,hide) based on horizontal scroll position

  const updateArrowVisibility = (scrollbarElement: HTMLDivElement | null | undefined) => {
    if (!scrollbarElement) {
      setIsLeftButtonVisible(false);
      setIsRightButtonVisible(false);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = scrollbarElement;

    const canScroll = scrollWidth > clientWidth;
    if (!canScroll) {
      setIsLeftButtonVisible(false);
      setIsRightButtonVisible(false);
      return;
    }

    const atStart = scrollLeft === 0;
    const atEnd = Math.ceil(scrollLeft + clientWidth) >= scrollWidth;

    setIsLeftButtonVisible(!atStart);
    setIsRightButtonVisible(!atEnd);
  };

  // on mount
  useEffect(() => {
    updateArrowVisibility(gridScrollbarElement);
  }, [gridScrollbarElement]);

  // on horizontal scroll
  useEffect(() => {
    if (!gridScrollbarElement) return;

    const handleHorizontalScroll = () => {
      updateArrowVisibility(gridScrollbarElement);
    };

    gridScrollbarElement.addEventListener("scroll", handleHorizontalScroll);
    return () => {
      gridScrollbarElement.removeEventListener("scroll", handleHorizontalScroll);
    };
  }, [gridScrollbarElement]);

  useEffect(() => {
    if (!gridRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const _entry of entries) {
        const gridScrollbar = gridRef.current?.querySelector(
          ".MuiDataGrid-scrollbar--horizontal",
        ) as HTMLDivElement;
        updateArrowVisibility(gridScrollbar);

        if (gridScrollbar) {
          setGridScrollbarElement(gridScrollbar);
        } else {
          setGridScrollbarElement(null);
        }
      }
    });
    resizeObserver.observe(gridRef.current);
  }, [gridRef.current]);

  // #endregion

  // #region Adjust horizontal scroll buttons position if we scroll top level app content

  const handleScroll = () => {
    if (!mainAppContentElement) return;

    const scrollTop = mainAppContentElement.scrollTop;
    const listHeight = gridMainElement?.clientHeight || 0;
    const scrollTopPlusDefaultOffset = buttonDefaultOffset + scrollTop;

    const newPosition =
      scrollTopPlusDefaultOffset + buttonDefaultOffset > listHeight
        ? listHeight - buttonDefaultOffset
        : scrollTopPlusDefaultOffset;

    leftButtonRef.current?.style.setProperty("top", `${newPosition}px`);
    rightButtonRef.current?.style.setProperty("top", `${newPosition}px`);
  };

  useEffect(() => {
    if (mainAppContentElement) {
      mainAppContentElement.addEventListener("scroll", handleScroll);

      return () => mainAppContentElement.removeEventListener("scroll", handleScroll);
    }
  }, [mainAppContentElement, gridMainElement]);

  // #endregion

  // #endregion

  return (
    <CustomDataGridContext.Provider value={customDataGridContext}>
      <MuiDataGrid
        ref={gridRef}
        sx={{
          "&.MuiDataGrid-root": {
            // border
            ...(borderVariant === "none" && {
              border: "none",
            }),
            ...(borderVariant === "bordered" && {
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: (th) => th.palette.divider,
            }),
            ...(borderVariant === "shadowed" && {
              border: "none",
              boxShadow: (th) => th.shadows[1],
            }),
          },
          "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
            outline: "none !important",
          },
          overflow: "hidden",

          ".MuiDataGrid-row-highlighted": {
            backgroundColor: (theme) => theme.palette.background.activeItemBackground,
          },

          // row hover
          "& .MuiDataGrid-row.Mui-hovered": {
            // backgroundColor: "rgba(0, 0, 0, 0.04)", // default MUI style
          },
          "& .MuiDataGrid-row:hover": {
            // backgroundColor: "rgba(0, 0, 0, 0.04)", // default MUI style

            // hide row number
            ".MuiDataGrid-customRowNumber": {
              display: "none",
            },
            // show row selection checkbox
            ".MuiDataGrid-customRowSelectionCheckbox": {
              display: "block",
            },
          },
          ...sx,
        }}
        apiRef={apiRef}
        columns={columnsComputed}
        // has an impact only when the data grid is rendered for the first time
        columnVisibilityModel={columnVisibilityModel}
        sortModel={sortModel}
        rows={rows || []}
        loading={isLoading}
        getRowId={getRowId}
        getRowClassName={(params) =>
          getRowIsHighlighted?.(params.row) ? "MuiDataGrid-row-highlighted" : ""
        }
        pageSizeOptions={PaginationHelper.pageSizeOptionsDataGrid}
        rowCount={rowCount}
        density='standard'
        paginationMode='server'
        filterMode='server'
        sortingMode='server'
        autosizeOnMount={false} // disable as we use resize & persist column width
        disableAutosize={false}
        autosizeOptions={{
          includeHeaders: true,
          expand: true,
        }}
        checkboxSelection={isRowCheckboxSelectionEnabled}
        rowSelectionModel={rowSelectionModel}
        paginationModel={paginationModel}
        disableVirtualization={false}
        disableColumnMenu={!isColumnMenuEnabledComputed}
        disableColumnResize={!isColumnResizeEnabled}
        disableColumnSelector={!isColumnSelectorEnabled}
        disableColumnSorting={!isColumnSortEnabledComputed}
        disableColumnFilter={!isColumnFilterEnabledComputed}
        disableDensitySelector={false}
        disableRowSelectionOnClick // disable as we use checkboxSelection
        disableMultipleRowSelection={false} // enable as we use checkboxSelection
        autoHeight={isAutoHeight}
        pagination
        rowHeight={rowHeight}
        // safely cast as we just added generic param to custom type for better typing
        getRowHeight={getRowHeight as (params: GridRowHeightParams) => GridRowHeightReturnValue}
        getEstimatedRowHeight={() => rowHeight}
        onRowClick={(params) => {
          console.log("onRowClick", params);
          onRowClick && onRowClick(params.row);

          // NB: Vadym commented this as it triggers redirect with little ability to handle row/cell click in other places.
          // This logic was moved to TableCellContent.
          // if (rowTo) {
          //   history.push(isFunction(rowTo) ? rowTo(params.row) : rowTo);
          // } else if (onRowClick) {
          //   onRowClick(params.row);
          // }
        }}
        onRowSelectionModelChange={(newRowSelectionModel, details) => {
          console.log("DataGrid.onRowSelectionModelChange", { newRowSelectionModel, details });
          onRowSelectionModelChange && onRowSelectionModelChange(newRowSelectionModel);
        }}
        onColumnVisibilityModelChange={(newModel, details) => {
          const newState: GridStateCommunity = {
            ...apiRef.current.state,
            columns: {
              ...apiRef.current.state.columns,
              columnVisibilityModel: newModel,
            },
          };
          console.log("DataGrid.onColumnVisibilityModelChange", { newModel, details, newState });
          setColumnVisibilityModel(newModel);

          columnVisibility?.onChange && columnVisibility.onChange({ ...newModel });
        }}
        onSortModelChange={(newModel, details) => {
          const newState: GridStateCommunity = {
            ...apiRef.current.state,
            sorting: {
              ...apiRef.current.state.sorting,
              sortModel: newModel,
            },
          };
          setSortModel(newModel);

          const newSortDefinition = SortHelper.mapGridSortModelToSortDefinition(newModel);
          sort?.onChange && sort?.onChange(newSortDefinition);

          console.log("DataGrid.onSortModelChange", {
            sortModel,
            newModel,
            details,
            newState,
            newSortDefinition,
          });
        }}
        onColumnResize={(params, event) => {
          console.log("DataGrid.onColumnResize", { params, event });
        }}
        onColumnWidthChange={(params, event) => {
          const newState: GridStateCommunity = apiRef.current.state;
          console.log("DataGrid.onColumnWidthChange", { params, event, newState });

          if (columnWidth) {
            columnWidth?.onChange &&
              columnWidth?.onChange({
                field: params.colDef.field,
                width: params.width,
              });
          }
        }}
        onPaginationModelChange={(newModel, details) => {
          handlePaginationModelChange(newModel);
        }}
        onRowCountChange={(newCount) => {
          console.log("DataGrid.onRowCountChange", { newCount });
          setRowCount(newCount);
        }}
        onStateChange={(state, event, details) => {
          // console.log("DataGrid.onStateChange", {
          //   state,
          //   event,
          //   details,
          // });
        }}
        slots={{
          // NB: there is a difference between how slots are specified:
          // 1. toolbar: CustomDataGridToolbar
          // 2. (props) => <CustomDataGridToolbar {...props} />
          // Don't know why, but in the second scenario the slot component can be re-rendered breaking the logic of the component.
          // E.g. Popovers can be auto-closed on re-render.
          pagination: CustomDataGridPagination,
          columnMenu: CustomDataGridColumnMenu,
          // filterPanel: CustomDataGridFilterPanel,
          toolbar: CustomDataGridToolbar,
          footer: CustomDataGridFooter,
          noRowsOverlay: NoDataAlert,
          noResultsOverlay: NoDataAlert,
          row: CustomRow,
        }}
        slotProps={{
          pagination: {
            isEnabled: isPaginationEnabledComputed,
            maxPageSize: dataGridMaxPageSize,
            // use custom callback to avoid issue with simultaneously setting page/pageSize via grid API.
            onChangeCustom: (newValue) => {
              const newModel: GridPaginationModel = {
                page: newValue.page,
                pageSize: newValue.pageSize,
              };
              handlePaginationModelChange(newModel);
            },
          },
          columnsManagement: {
            disableResetButton: false,
            disableShowHideToggle: false,
            getTogglableColumns: (columns2) => {
              const result = columns2
                .filter(
                  (column) =>
                    column.field !== rowCheckboxSelectionField &&
                    column.field !== rowNumberField &&
                    (column.hideable || isNil(column.hideable)) &&
                    // ignore columns that were created for internal purposes such as filtering, API querying, etc
                    !(startsWith(column.field, "__") && endsWith(column.field, "__")),
                )
                .map((column) => column.field);

              return result;
            },
          },
          // columnMenu: {
          //   openFilterPanel: (targetColumn) => {
          //     apiRef.current?.showFilterPanel();
          //     setTargetFilterColumn(targetColumn);
          //   },
          // },
          // filterPanel: { column: targetFilterColumn, filterValues },
          row: {
            style: {
              // // NB: see onRowClick comment.
              // cursor: rowTo ? "pointer" : undefined,
            },
          },
          toolbar: {
            tabs: tabs,
            sort: sortComputed,
            quickFilter: quickFilter,
            filters: filters,
            refetch: refetch,
          },
          footer: {
            isPaginationEnabled: isPaginationEnabledComputed,
          },
          noRowsOverlay: {
            style: {
              position: "relative",
              zIndex: 1,
            },
          },
        }}
      />

      {gridMainElement &&
        gridScrollbarElement &&
        createPortal(
          <>
            {isLeftButtonVisible && (
              <AppIconButton
                ref={leftButtonRef}
                variant='contained'
                onClick={handleScrollLeftClick}
                sx={{
                  position: "absolute",
                  top: `${buttonDefaultOffset}px`,
                  left: 16,
                  zIndex: 10,
                  minWidth: "32px",
                  padding: 1,
                  boxShadow: (th) => th.shadows[1],
                }}
              >
                <AppIcon of='arrowBack' />
              </AppIconButton>
            )}

            {isRightButtonVisible && (
              <AppIconButton
                ref={rightButtonRef}
                variant='contained'
                onClick={handleScrollRightClick}
                sx={{
                  position: "absolute",
                  top: `${buttonDefaultOffset}px`,
                  right: 16,
                  zIndex: 10,
                  minWidth: "32px",
                  padding: 1,
                  boxShadow: (th) => th.shadows[1],
                }}
              >
                <AppIcon of='arrowForward' />
              </AppIconButton>
            )}

            {/* left gradient blur */}
            {isLeftButtonVisible && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  height: `calc(100% - ${2 * 0}px)`,
                  width: "100px",
                  pointerEvents: "none",
                  background: (t) =>
                    t.palette.mode === "light"
                      ? "linear-gradient(90deg,rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.33) 100%)"
                      : "linear-gradient(90deg,rgba(33, 43, 51, 1) 0%, rgba(33, 43, 51, 0) 100%)",
                  zIndex: 5,
                }}
              />
            )}

            {/* right gradient blur */}
            {isRightButtonVisible && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  height: `calc(100% - ${2 * 0}px)`,
                  width: "100px",
                  pointerEvents: "none",
                  background: (t) =>
                    t.palette.mode === "light"
                      ? "linear-gradient(90deg,rgba(255, 255, 255, 0.33) 0%, rgba(255, 255, 255, 1) 100%)"
                      : "linear-gradient(90deg,rgba(33, 43, 51, 0) 0%, rgba(33, 43, 51, 1) 100%)",
                  zIndex: 5,
                }}
              />
            )}
          </>,
          gridMainElement,
        )}
    </CustomDataGridContext.Provider>
  );
}
